import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {ModalService} from '~shared/components/elements/global-modal/modal.service';
import {ActionButtonType} from '~core/enum';
import {TixxSelectSubmissionService} from '~core/services';
import {ReminderStatusTranslationsService, TooltipTranslationService} from '~shared/translations';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {ActionButton, ActionButtonEvent, Customer, TixxSelectSubmission} from '~core/models';

/**
 * Handles tixx select requests
 */
@UntilDestroy()
@Component({
  selector: 'app-tixx-select-list',
  templateUrl: './tixx-select-list.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class TixxSelectListComponent implements OnInit, OnDestroy {
  public currentSubmissions: Array<TixxSelectSubmission>;
  public actionButtons: Array<ActionButton> = [];

  public columns: Array<string> = [];
  public keys: Array<Array<any>> = [];

  public i18nDeleteTitle: string;
  public i18nDeleteParagraph: string;

  @Input() public customer: Customer;
  @Input() public isPanelOpened: boolean = true;
  @Output() public isPanelOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public customerOverviewService: CustomerOverViewService,
    public submissionService: TixxSelectSubmissionService,
    public reminderStatusTranslations: ReminderStatusTranslationsService,
    public tablesTranslations: TablesTranslations,
    public tooltipTranslations: TooltipTranslationService,
    public modalService: ModalService,
    public customerService: CustomerOverViewService,
  ) {
    this.submissionService.castSubmissionList.pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data != null) {
          this.currentSubmissions = data;
        }
      });

    this.actionButtons.push(
      new ActionButton({
        type: ActionButtonType.DELETE,
        icon: '#esf-icon-trash',
        tooltip: this.tooltipTranslations.i18nDelete,
        disabled: {key: 'publicLottery.status.name', value: val => val === 'execution' || val === 'completed'}
      })
    );
    this.columns.push(this.tablesTranslations.i18nDateOfSubmission, this.tablesTranslations.i18nEvent, this.tablesTranslations.i18nStatus);
    this.keys.push(['submittedAt', 'fullName', 'status']);
  }

  public ngOnInit(): void {
    this.getTixxSelectSubmissions(this.customer.id);
    this.i18nDeleteTitle = $localize`:@@tixxSelect.modalTitle.deleteTicketRequest:Delete ticket request?`;
    this.i18nDeleteParagraph = $localize`:@@tixxSelect.modalParagraph.deleteTicketRequest:This action will delete the entire request including all submitted information. The customer wont be notified automatically but will be able to submit a new request if this is still feasible. Delete this ticket request?`;
  }

  public ngOnDestroy(): void {
  }

  /**
   * Get the tixx select submissions for the current customer.
   */
  public getTixxSelectSubmissions(id: number): any {
    this.submissionService.getAll(id).pipe(untilDestroyed(this))
      .subscribe((data: Array<TixxSelectSubmission>) => {
        this.submissionService.setSubmissionsList(data);
      });
  }

  public async showDeleteModal(id: string): Promise<any> {
    this.modalService.setModalOptions(this.i18nDeleteTitle, this.i18nDeleteParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.deleteTicketRequest(id);
    }
  }

  public deleteTicketRequest(id: string): void {
    this.submissionService.delete(id)
      .subscribe(() => {
        this.submissionService.deleteSubmissionsList(id);
        this.modalService.setStatus(false);
      });
  }

  /**
   * Open the submission details modal and display more information.
   */
  public viewSubmissionDetails(submission: TixxSelectSubmission): any {
    this.customerService.toggleAllModal(false);
    this.customerService.toggleViewSubmissionDetailsModal(true);
    this.submissionService.setSelectedSubmission(submission);
  }

  /**
   * Checks which action button was clicked
   */
  public actionButtonClicked(actionButton: ActionButtonEvent<any>): void {
    switch (actionButton.type) {
      case ActionButtonType.DELETE:
        this.showDeleteModal(actionButton.data.id);
        break;
      default:
    }
  }
}
